/**
 * Host CPU/RAM/disk panel (ao_planning_vm_resource_monitor_2026_07_27) —
 * top-row, left of the Backlog summary. Single-VM architecture (2026-06-27):
 * the central API IS the planning VM, so this connects directly rather than
 * through the /api/vms/<id>/* proxy.
 *
 * Live over a WebSocket (GET /ws/vm-resources) instead of the usual 30s
 * fetch-poll — the operator wants to see a CPU/disk spike as it happens, not
 * up to 30s late. Server pushes a snapshot every 5s
 * (server/routes/vms.py:VM_RESOURCES_PUSH_INTERVAL_SECONDS). Reconnects with
 * a fixed backoff on drop; the connection dot in the panel header shows
 * live / connecting / disconnected so a stale reconnect loop is visible
 * instead of silently going stale.
 *
 * Display logic lives in pure, exported mappers (vitest-covered in
 * VmResources.test.ts) — the React shell only owns the socket + layout,
 * mirroring FleetGit.tsx / FleetKpis.tsx.
 *
 * Staleness (2026-09-02, resource_history_sampler_wedged_active_but_not_writing_2026_09_02.md):
 * a connected socket can faithfully re-push a FROZEN snapshot for hours if the standalone
 * sampler wedges (systemd's own liveness check can't see it — the process stays alive, just
 * stops writing). `data.stale`/`stale_reason` are computed server-side against "now", never
 * inferred here from an unchanging value, and drive both a banner and the connection dot's
 * color — a green dot alone is no longer read as "the data is live."
 */

import { Fragment, lazy, Suspense, useEffect, useState } from "react";

import { api, HttpError } from "./api";
import { Icon, Panel } from "./components";
import type { HostCapacityRowView, HostResources, ScaleHostResponse } from "./types";
import { fmtAgo, formatBytes } from "./utils";

// Lazy: recharts (~380KB) must never land in the initial bundle — see
// HostResourceHistoryChart.tsx's own module docstring for why this is a separate
// file rather than a static import here.
const HostResourceHistoryChart = lazy(() =>
  import("./HostResourceHistoryChart").then((m) => ({ default: m.HostResourceHistoryChart })),
);

// ── pure display mappers (vitest-covered) ──────────────────────────────────

const _RED = "var(--status-stale)";
const _AMBER = "var(--status-blocked)";
const _GREEN = "var(--status-working)";
const _MUTE = "var(--fg-2)";

const RECONNECT_DELAY_MS = 3_000;

/** Busy% -> colour token. HIGH usage is the bad direction here (the inverse
 * of e.g. free-disk-pct elsewhere in this codebase). null (probe unreadable
 * or no snapshot yet) is muted, never a misleading green. */
export function resourceTone(pct: number | null): string {
  if (pct === null) return _MUTE;
  if (pct >= 90) return _RED;
  if (pct >= 75) return _AMBER;
  return _GREEN;
}

export function formatPercent(pct: number | null): string {
  return pct === null ? "—" : `${pct.toFixed(0)}%`;
}

/** "<used> / <total>" in MB/GB (formatBytes, 1024-based) — never raw bytes,
 * never NaN when a probe came back null. */
export function formatUsedOfTotal(usedBytes: number | null, totalBytes: number | null): string {
  if (usedBytes === null || totalBytes === null) return "—";
  return `${formatBytes(usedBytes)} / ${formatBytes(totalBytes)}`;
}

/** "load 4.2" (1min) — "—" when unavailable (e.g. a non-Linux dev box). Deliberately just the
 * 1min figure in the tile (5min/15min are in the tooltip via the caller) — a glance needs one
 * number, trend needs the other two. */
export function formatLoadAvg(load1: number | null): string {
  return load1 === null ? "—" : `load ${load1.toFixed(1)}`;
}

/** iowait% -> colour token. Same thresholds as resourceTone, but iowait is meaningful at LOWER
 * absolute values than CPU/RAM/disk saturation — sustained double-digit iowait already means the
 * box is disk-bound (the 2026-07-28 incident measured 66-93%), so this uses its own scale rather
 * than reusing resourceTone's 75/90 cutoffs, which would stay green through a real bottleneck. */
export function iowaitTone(pct: number | null): string {
  if (pct === null) return _MUTE;
  if (pct >= 50) return _RED;
  if (pct >= 20) return _AMBER;
  return _GREEN;
}

/** swap% -> colour token. Shares iowaitTone's lower-threshold scale rather than resourceTone's
 * 75/90 — unlike RAM/disk (where "high but not full" is normal), a host climbing into swap AT
 * ALL is already unusual on a properly-provisioned box, and swap fully exhausted (100%, thrashing
 * with zero memory headroom left) is exactly the state a 2026-07-29 investigation found masked
 * behind an unremarkable-looking RAM% + a LOW CPU% (the thrashing showed up as iowait, not
 * compute) — this tile exists so that state is never invisible again. */
export const swapTone = iowaitTone;

/** Data-freshness tone, independent of the WebSocket transport's own connected/disconnected
 * state (2026-09-02, resource_history_sampler_wedged_active_but_not_writing_2026_09_02.md) —
 * the documented incident was a socket that stayed genuinely OPEN for hours while faithfully
 * re-pushing a frozen sampler snapshot every 5s, so "the socket is connected" and "the data
 * is live" are two different claims and must render as two different signals. Red when
 * stale, matching resourceTone's "bad direction is red" convention even though this is a
 * boolean rather than a percent. */
export function dataStaleTone(stale: boolean): string {
  return stale ? _RED : _GREEN;
}

/** Absolute WebSocket URL for `wsPath` (defaults to /ws/vm-resources), derived from the
 * dashboard's (http/https) API baseUrl. Handles the standalone-mode empty-string baseUrl
 * (resolves to the page's own origin) same as the REST client does. The `wsPath` override
 * is what lets a second `<VmResources>` instance point at a different push endpoint — e.g.
 * `/ws/vm-resources/ci-host` (routes/ci_host.py) — while reusing every rendering/mapper
 * function unmodified (ci_host_saturation_remediation_and_observability_2026_08_30.md
 * Phase 1: "point the existing VmResources component at the CI host").
 *
 * `ticket` is a one-use ws-ticket minted via `api.mintWsTicket` (`POST /api/ws-ticket`),
 * NEVER the raw bearer JWT — a JWT living in this URL is logged in plaintext on every
 * connect (access log / journal / any proxy log / browser history), the leak
 * `ao_dashboard_jwt_localstorage_ws_query_string_unsandboxed_iframe_2026_09_04.md` todo 4
 * closes on the client side (todo 3 already made the server reject a raw JWT here). */
export function vmResourcesWsUrl(
  baseUrl: string,
  ticket: string | null,
  origin: string,
  wsPath: string = "/ws/vm-resources",
): string {
  const url = new URL(`${baseUrl}${wsPath}`, origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  if (ticket) url.searchParams.set("token", ticket);
  return url.toString();
}

type ConnState = "connecting" | "open" | "closed";

export interface UseVmResourcesResult {
  data: HostResources | null;
  connState: ConnState;
}

/** Owns the `/ws/vm-resources` socket lifecycle (connect, reconnect-with-backoff, parse) —
 * extracted from the `VmResources` component so a second, independent consumer
 * (`VmResourcesStaleBanner`, rendered at page level rather than inside this tile) can read
 * the identical live signal. `enabled=false` skips connecting entirely (used at page level
 * to defer opening a socket until the dashboard has actually loaded, matching the tile's own
 * effective lifecycle — it only mounts once `state !== null`).
 *
 * Mints a fresh one-use ws-ticket (`api.mintWsTicket`) on every connect attempt — including
 * every reconnect — rather than threading the dashboard's own bearer token through; the
 * token lives only in `api.ts`'s in-memory auth state / the session cookie, both of which
 * `mintWsTicket` rides on automatically, so this hook never needs the raw JWT at all
 * (`ao_dashboard_jwt_localstorage_ws_query_string_unsandboxed_iframe_2026_09_04.md` todo 4). */
export function useVmResources(
  baseUrl: string,
  wsPath: string = "/ws/vm-resources",
  enabled: boolean = true,
): UseVmResourcesResult {
  const [data, setData] = useState<HostResources | null>(null);
  const [connState, setConnState] = useState<ConnState>("connecting");

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let socket: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    async function connect() {
      if (cancelled) return;
      setConnState("connecting");
      let ticket: string | null = null;
      try {
        ticket = (await api.mintWsTicket(baseUrl)).ticket;
      } catch {
        // No session to mint against (or a transient network/5xx) — connect ticket-less;
        // authenticate_websocket's trusted-loopback fallback covers same-box dev callers,
        // and any genuinely unauthorized attempt is rejected server-side either way. The
        // reconnect loop below retries the mint on every subsequent attempt regardless.
      }
      if (cancelled) return;
      socket = new WebSocket(vmResourcesWsUrl(baseUrl, ticket, window.location.origin, wsPath));
      socket.onopen = () => {
        if (!cancelled) setConnState("open");
      };
      socket.onmessage = (event) => {
        if (cancelled) return;
        try {
          setData(JSON.parse(event.data as string) as HostResources);
        } catch {
          // Malformed push — skip this tick, keep showing the last good snapshot.
        }
      };
      socket.onclose = () => {
        if (cancelled) return;
        setConnState("closed");
        reconnectTimer = setTimeout(connect, RECONNECT_DELAY_MS);
      };
      socket.onerror = () => {
        socket?.close();
      };
    }

    void connect();
    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      socket?.close();
    };
  }, [baseUrl, wsPath, enabled]);

  return { data, connState };
}

/** Page-level counterpart to the in-tile banner below — same generic `data.stale` signal,
 * styled with the app's shared `.error-banner` treatment (see `ErrorBanner`/`ActionErrorToast`
 * in App.tsx) so a wedged sampler reads with the SAME prominence as a connection-loss or
 * action-failure alert, instead of the thin line buried under four healthy-looking tiles that
 * this replaces (resource_history_sampler_wedged_active_but_not_writing_2026_09_02.md,
 * operator ask 2026-09-03: "the eye lands on four healthy-looking numbers and skips the one
 * line saying those numbers are 14 hours stale"). Renders nothing when the signal is fresh or
 * not yet loaded — mount unconditionally, it self-hides. */
export function VmResourcesStaleBanner({ data }: { data: HostResources | null }) {
  if (!data?.stale) return null;
  return (
    <div className="error-banner" data-testid="vm-resources-stale-page-banner">
      <span className="icon">
        <Icon.Warn />
      </span>
      <div style={{ flex: 1 }}>
        <div className="title">Host resource sampler not reporting</div>
        <div className="url">
          {data.stale_reason ?? "resource data looks stale"}
          {data.sampled_at && <> — last sample {fmtAgo(data.sampled_at)}</>}
        </div>
      </div>
    </div>
  );
}

// ── panel ────────────────────────────────────────────────────────────────

interface VmResourcesProps {
  baseUrl: string;
  /** Overrides the WS path (default `/ws/vm-resources` = this backend's own host). Pass
   * `/ws/vm-resources/ci-host` to render the CI runner host instead. */
  wsPath?: string;
  /** Overrides the panel title (default "Host Resources") — set a distinct title when
   * rendering a non-self host so it isn't confused with this backend's own resources. */
  title?: string;
}

export function VmResources({ baseUrl, wsPath = "/ws/vm-resources", title = "Host Resources" }: VmResourcesProps) {
  const { data, connState } = useVmResources(baseUrl, wsPath);
  const dataStale = data?.stale ?? false;

  return (
    <Panel title={title} tone="resources" flush right={<ConnDot state={connState} dataStale={dataStale} />}>
      {dataStale && (
        <div
          data-testid="vm-resources-stale-banner"
          style={{ color: dataStaleTone(true), padding: "0 12px 8px", fontSize: 11 }}
        >
          ⚠ {data?.stale_reason ?? "resource data looks stale"}
          {data?.sampled_at && <> — last sample {fmtAgo(data.sampled_at)}</>}
        </div>
      )}
      <div
        data-testid="vm-resources-tiles"
        style={{
          display: "flex",
          gap: 8,
          padding: "10px 12px",
          flexWrap: "wrap",
          // Degrade the tiles themselves when stale (2026-09-03 operator ask, same doc as
          // above): the in-tile banner alone left four large green numbers reading as
          // healthy right below the one line saying they were 14h old — grayscale + dimmed
          // opacity makes a stale reading unmistakable at a glance, not just in fine print.
          filter: dataStale ? "grayscale(1)" : undefined,
          opacity: dataStale ? 0.55 : 1,
        }}
      >
        <ResourceTile label="CPU" pct={data?.cpu_percent ?? null} sub={data ? `${data.cpu_count} cores` : "—"} />
        <ResourceTile
          label="I/O Wait"
          pct={data?.iowait_percent ?? null}
          sub={formatLoadAvg(data?.load_avg_1m ?? null)}
          tone={iowaitTone}
          title={
            data?.load_avg_5m != null && data?.load_avg_15m != null
              ? `load avg 1/5/15min: ${data.load_avg_1m?.toFixed(1)} / ${data.load_avg_5m.toFixed(1)} / ${data.load_avg_15m.toFixed(1)}`
              : undefined
          }
        />
        <ResourceTile
          label="RAM"
          pct={data?.ram_percent ?? null}
          sub={formatUsedOfTotal(data?.ram_used_bytes ?? null, data?.ram_total_bytes ?? null)}
        />
        <ResourceTile
          label="Swap"
          pct={data?.swap_percent ?? null}
          sub={formatUsedOfTotal(data?.swap_used_bytes ?? null, data?.swap_total_bytes ?? null)}
          tone={swapTone}
        />
        <ResourceTile
          label="Disk"
          pct={data?.disk_percent ?? null}
          sub={formatUsedOfTotal(data?.disk_used_bytes ?? null, data?.disk_total_bytes ?? null)}
        />
      </div>
    </Panel>
  );
}

function ResourceTile({
  label,
  pct,
  sub,
  tone = resourceTone,
  title,
}: {
  label: string;
  pct: number | null;
  sub: string;
  /** Defaults to resourceTone (75/90 thresholds) — pass iowaitTone for the I/O Wait tile, whose
   * "already a problem" threshold is much lower. */
  tone?: (pct: number | null) => string;
  title?: string;
}) {
  return (
    <div
      title={title}
      style={{
        background: "var(--bg-1)",
        border: "1px solid var(--bg-2)",
        borderRadius: 6,
        padding: "8px 14px",
        minWidth: 96,
        flex: "1 1 0",
      }}
    >
      <div className="mono" style={{ fontSize: 18, fontWeight: 600, color: tone(pct) }}>
        {formatPercent(pct)}
      </div>
      <div style={{ fontSize: 11, color: _MUTE, marginTop: 2 }}>{label}</div>
      <div className="mono" style={{ fontSize: 10.5, color: _MUTE, marginTop: 1 }}>
        {sub}
      </div>
    </div>
  );
}

function ConnDot({ state, dataStale }: { state: ConnState; dataStale: boolean }) {
  const color = state === "open" ? dataStaleTone(dataStale) : state === "connecting" ? _AMBER : _RED;
  const title =
    state === "open"
      ? dataStale
        ? "connected, but the sampler data looks stale — see the banner above"
        : "live"
      : state === "connecting"
        ? "connecting…"
        : "disconnected — retrying";
  return (
    <span
      title={title}
      style={{ width: 7, height: 7, borderRadius: "50%", background: color, display: "inline-block" }}
    />
  );
}

// ── multi-host resources table (host-resources-panel-blank-and-multihost-table_2026_09_09,
// ao_fleet_slot_resource_ui_and_scale_controls_2026_09_05.md todo 2 — "one row or card per
// host") — the OTHER mount point for this file's panel, replacing the single-host tile set
// above at the two App.tsx call sites: an operator with more than one AO backend running
// (central + N v2 nodes) never saw any of the others' resources anywhere in the dashboard
// — this table is that view. Polled (GET /api/hosts/capacity), not pushed: there is one
// row per host rather than one persistent stream, so a REST poll is the natural fit (and
// keeps every row's underlying reads — resource_history.latest_from_disk() for central,
// NodeRow for a node — exactly the SAME read paths the rest of this dashboard already
// uses, none of them WebSocket-shaped). Reuses this file's own resourceTone/formatPercent/
// formatUsedOfTotal/swapTone/dataStaleTone mappers so a host row and the CI-host tile above
// render identical percentages identically.

const HOSTS_CAPACITY_POLL_INTERVAL_MS = 5_000;

export interface UseHostsCapacityResult {
  hosts: HostCapacityRowView[];
  error: string | null;
  loaded: boolean;
}

export function useHostsCapacity(baseUrl: string): UseHostsCapacityResult {
  const [hosts, setHosts] = useState<HostCapacityRowView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchOnce() {
      try {
        const resp = await api.hostsCapacity(baseUrl);
        if (!cancelled) {
          setHosts(resp.hosts);
          setError(null);
          setLoaded(true);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "fetch failed");
          setLoaded(true);
        }
      }
    }

    void fetchOnce();
    const interval = setInterval(fetchOnce, HOSTS_CAPACITY_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [baseUrl]);

  return { hosts, error, loaded };
}

/** "N/M" slot-reporting honesty (plan's own done-when: "partial data must be labelled as
 * partial rather than averaged into a confident wrong number") — never just the total. A
 * host this backend has never heard from at all (excluded from the list endpoint's own
 * response — see list_hosts_capacity's docstring) never reaches this component, so
 * `ever_reported` is always true here; the branch stays as documentation of that contract
 * rather than dead code that silently stops being true. */
export function formatSlotsReporting(row: HostCapacityRowView): string {
  if (!row.ever_reported) return "never reported";
  return `${row.slots_reporting_resources}/${row.slots_total}`;
}

/** The scale control's error surface (ao_fleet_slot_resource_ui_and_scale_controls_
 * 2026_09_05.md todo 3 — "surface a refusal with the measured reason the backend
 * returned, rather than a generic failure"). `scale_host`'s 404/409/422 all carry a
 * real, specific reason in their JSON `detail` (a host that's never heartbeated, one
 * whose current state is stale/unknown, a RAM-ceiling refusal, an above-declared-range
 * request) — this pulls that out rather than showing "422 Unprocessable Entity". Pure
 * (vitest-covered): takes whatever `catch` handed it, never assumes the shape. */
export function scaleHostErrorMessage(err: unknown): string {
  if (err instanceof HttpError) {
    try {
      const parsed = JSON.parse(err.body) as { detail?: string };
      if (typeof parsed.detail === "string" && parsed.detail) return parsed.detail;
    } catch {
      // body wasn't JSON — fall through to the generic status-line message below.
    }
    return `${err.status} ${err.statusText}`;
  }
  return err instanceof Error ? err.message : "scale request failed";
}

/** "What will happen before it happens" (ao_fleet_slot_resource_ui_and_scale_controls_
 * 2026_09_05.md todo 3's own done-when) — the actuation itself is asynchronous (next
 * heartbeat/dispatch tick), so this text IS the "before it happens" view: which slots
 * start, which drain (busy — a graceful scale-down, never an apparent no-op), which
 * retire immediately (were already idle). Pure (vitest-covered). */
export function formatScaleResult(resp: ScaleHostResponse): string {
  const parts: string[] = [`target set to ${resp.target_slot_count} of ${resp.slots_total} declared`];
  if (resp.newly_eligible_slot_ids.length > 0) {
    parts.push(`starting: ${resp.newly_eligible_slot_ids.map((id) => `#${id}`).join(", ")}`);
  }
  if (resp.draining_slot_ids.length > 0) {
    parts.push(`draining (busy, retires once idle): ${resp.draining_slot_ids.map((id) => `#${id}`).join(", ")}`);
  }
  if (resp.retiring_now_slot_ids.length > 0) {
    parts.push(`retiring now (was idle): ${resp.retiring_now_slot_ids.map((id) => `#${id}`).join(", ")}`);
  }
  if (parts.length === 1) {
    parts.push("no change to which slots are active");
  }
  return parts.join(" — ");
}

/** R17's scale control (ao_fleet_slot_resource_ui_and_scale_controls_2026_09_05.md
 * todo 3) — lives inside a row's own expanded "card" area (alongside the history
 * chart), for every host including central (2026-09-27: `HostsResourcesTable`'s
 * `expandable` gate stopped excluding central — see `isHostExpandable`'s own
 * docstring). NOTE this control itself is unconditional here: `scale_host` still
 * 404s central (it has no `NodeRow` — central has no slots to scale), so central's
 * expanded row shows a scale control that always errors on submit; that pre-existing
 * gap was not introduced by the 2026-09-27 fix and is out of this fix's scope, but is
 * worth a follow-up (hide `HostScaleControl` for `host.is_central`, or have the
 * control read a clean "not applicable" state instead of a raw 404). `stopPropagation`
 * on the wrapper keeps clicks inside this control from also toggling the row's own
 * expand/collapse. */
function HostScaleControl({ baseUrl, host }: { baseUrl: string; host: HostCapacityRowView }) {
  const [target, setTarget] = useState<number>(host.slots_total);
  const [result, setResult] = useState<ScaleHostResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const resp = await api.scaleHost(baseUrl, host.host_id, target);
      setResult(resp);
    } catch (err) {
      setError(scaleHostErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      data-testid={`host-scale-control-${host.host_id}`}
      style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <label style={{ fontSize: 11, color: _MUTE }} htmlFor={`host-scale-input-${host.host_id}`}>
          Target slots (of {host.slots_total} declared):
        </label>
        <input
          id={`host-scale-input-${host.host_id}`}
          data-testid={`host-scale-input-${host.host_id}`}
          type="number"
          min={0}
          max={host.slots_total}
          value={target}
          onChange={(e) => setTarget(Number(e.target.value))}
          style={{ width: 50 }}
        />
        <button data-testid={`host-scale-submit-${host.host_id}`} onClick={() => void submit()} disabled={submitting}>
          {submitting ? "Scaling…" : "Scale"}
        </button>
      </div>
      {error && (
        <div data-testid={`host-scale-error-${host.host_id}`} style={{ color: _RED, fontSize: 11, marginTop: 4 }}>
          ⚠ {error}
        </div>
      )}
      {result && (
        <div data-testid={`host-scale-result-${host.host_id}`} style={{ fontSize: 11, marginTop: 4, color: _MUTE }}>
          {formatScaleResult(result)}
        </div>
      )}
    </div>
  );
}

/** Whether a host's row can expand to show its history chart + scale control.
 * Every host, central included, since 2026-09-27 (server/routes/hosts.py's
 * get_host_resource_history now reads central's own pre-existing sampler-JSONL
 * log for that id — see HostResourceHistoryChart's own docstring — rather than
 * being node-only). Before that fix this returned `!host.is_central`: kept as
 * its own named function (never inlined as `true`) so a future reintroduction
 * of a central-exclusion is a one-line diff this test file's own assertion
 * catches, rather than a silent revert. Pure (vitest-covered). */
export function isHostExpandable(_host: HostCapacityRowView): boolean {
  return true;
}

export function HostsResourcesTable({ baseUrl }: { baseUrl: string }) {
  const { hosts, error, loaded } = useHostsCapacity(baseUrl);
  // Which row is expanded to show its history chart — at most one at a time.
  const [expandedHostId, setExpandedHostId] = useState<string | null>(null);

  return (
    <Panel title="Host Resources" tone="resources" flush>
      {error && <div style={{ color: dataStaleTone(true), padding: "8px 12px", fontSize: 11 }}>⚠ {error}</div>}
      {!error && loaded && hosts.length === 0 && (
        <div style={{ padding: "10px 12px", fontSize: 12, color: _MUTE }}>no hosts reporting yet</div>
      )}
      {hosts.length > 0 && (
        <table
          data-testid="host-resources-table"
          className="mono"
          style={{ width: "100%", fontSize: 11.5, borderCollapse: "collapse" }}
        >
          <thead>
            <tr style={{ textAlign: "left", color: _MUTE, fontSize: 10.5 }}>
              <th style={{ padding: "4px 12px", fontWeight: 400 }}>Host</th>
              <th style={{ padding: "4px 8px", fontWeight: 400 }}>CPU</th>
              <th style={{ padding: "4px 8px", fontWeight: 400 }}>RAM</th>
              <th style={{ padding: "4px 8px", fontWeight: 400 }}>Swap</th>
              <th style={{ padding: "4px 8px", fontWeight: 400 }}>Disk</th>
              <th style={{ padding: "4px 12px", fontWeight: 400 }}>Slots</th>
            </tr>
          </thead>
          <tbody>
            {hosts.map((h) => {
              const expandable = isHostExpandable(h);
              const isExpanded = expandedHostId === h.host_id;
              return (
                <Fragment key={h.host_id}>
                  <tr
                    data-testid={`host-resources-row-${h.host_id}`}
                    data-central={h.is_central ? "true" : undefined}
                    title={h.stale ? (h.stale_reason ?? "resource data looks stale") : undefined}
                    style={{
                      filter: h.stale ? "grayscale(1)" : undefined,
                      opacity: h.stale ? 0.55 : 1,
                      cursor: expandable ? "pointer" : undefined,
                    }}
                    onClick={expandable ? () => setExpandedHostId(isExpanded ? null : h.host_id) : undefined}
                  >
                    <td style={{ padding: "4px 12px" }}>
                      {expandable && (
                        <span
                          data-testid={`host-resources-expand-${h.host_id}`}
                          style={{ color: _MUTE, marginRight: 4, display: "inline-block", width: 8 }}
                        >
                          {isExpanded ? "▾" : "▸"}
                        </span>
                      )}
                      {h.host_id}
                      {h.is_central && (
                        <span data-testid="host-resources-row-central" style={{ color: _MUTE, marginLeft: 4 }}>
                          (central)
                        </span>
                      )}
                      {!h.is_central && h.label && <span style={{ color: _MUTE, marginLeft: 4 }}>({h.label})</span>}
                      {h.stale && (
                        <span style={{ color: dataStaleTone(true), marginLeft: 4 }} title={h.stale_reason ?? undefined}>
                          ⚠
                        </span>
                      )}
                    </td>
                    <td style={{ padding: "4px 8px", color: resourceTone(h.host_cpu_pct) }}>
                      {formatPercent(h.host_cpu_pct)}
                    </td>
                    <td style={{ padding: "4px 8px", color: resourceTone(h.host_ram_pct) }}>
                      {formatPercent(h.host_ram_pct)}
                    </td>
                    <td style={{ padding: "4px 8px", color: swapTone(h.host_swap_pct) }}>
                      {formatPercent(h.host_swap_pct)}
                    </td>
                    <td style={{ padding: "4px 8px", color: resourceTone(h.host_disk_pct) }}>
                      {formatPercent(h.host_disk_pct)}
                    </td>
                    <td style={{ padding: "4px 12px" }}>{formatSlotsReporting(h)}</td>
                  </tr>
                  {isExpanded && (
                    <tr data-testid={`host-resources-history-${h.host_id}`}>
                      <td colSpan={6} style={{ padding: 0, background: "var(--bg-2)" }}>
                        <HostScaleControl baseUrl={baseUrl} host={h} />
                        <Suspense
                          fallback={
                            <div style={{ fontSize: 11, color: _MUTE, padding: "6px 12px" }}>loading chart…</div>
                          }
                        >
                          <HostResourceHistoryChart baseUrl={baseUrl} hostId={h.host_id} />
                        </Suspense>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </Panel>
  );
}
